import React, { useState, useEffect } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import useApi from '../../hooks/useApi';

const EditStudent = ({ student, onClose }) => {
  const { bearerAuth, publicAuth } = useApi();
  
  // Form data state
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    contact: "",
    zipcode: "",
    dob: "",
    gender: "",
    city_id: "",
    assignSchool: "",
    course_id: "",
    enrollment_for: "self",
    admin_exact_start_at: "",
    admin_time_slot: "",
    inc_sat_sun: true,
    admin_note: "",
    status: 1
  });

  // UI state
  const [cities, setCities] = useState([]);
  const [schools, setSchools] = useState([]);
  const [courses, setCourses] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isFormReady, setIsFormReady] = useState(false);
  
  // Calendar and date range state
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [includeWeekends, setIncludeWeekends] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [selectedCourseDuration, setSelectedCourseDuration] = useState(0);

  const enrollmentOptions = [
    { value: "self", label: "Self" },
    { value: "parent", label: "Parent" },
    { value: "other", label: "Other" }
  ];

  function normalizeSlotText(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const parts = raw.split("-").map((part) => part.trim()).filter(Boolean);
    if (parts.length < 2) return raw;
    const start = String(parts[0]).slice(0, 8);
    const end = String(parts[1]).slice(0, 8);
    return `${start} - ${end}`;
  }

  // Initialize form with student data
  useEffect(() => {
    if (student) {
      loadStudentData();
    }
  }, [student]);

  // Calculate date range when course or weekends selection changes
  useEffect(() => {
    if (formData.course_id && selectedCourseDuration > 0) {
      calculateDateRange();
    }
  }, [formData.course_id, selectedCourseDuration, includeWeekends, selectedDate]);

  // Fetch time slots when date and school are selected
  useEffect(() => {
    if (selectedDate && formData.assignSchool && isFormReady) {
      fetchTimeSlots(selectedDate);
    }
  }, [selectedDate, formData.assignSchool, isFormReady]);

  const loadStudentData = async () => {
    if (!student) return;
    
    try {
      // Parse student data to match form structure
      const enrollment = student.enrollment || {};
      const drivingSchool = enrollment.driving_school || {};
      const course = enrollment.course || {};
      
      // Set basic form data
      const initialFormData = {
        full_name: student.full_name || student.full_name || "",
        email: student.email || "",
        contact: student.contact || "",
        zipcode: student.zipcode || "",
        dob: enrollment.dob || "",
        gender: enrollment.gender || "",
        city_id: drivingSchool.city_id || "",
        assignSchool: drivingSchool.id || "",
        course_id: course.id || "",
        enrollment_for: enrollment.enrollment_for || "self",
        admin_exact_start_at: enrollment.start_date ? 
          new Date(enrollment.start_date).toISOString().split('T')[0] : "",
        admin_time_slot: normalizeSlotText(enrollment.time_slot || ""),
        inc_sat_sun: enrollment.include_weekends || true,
        admin_note: enrollment.notes || "",
        status: student.status || 1
      };

      setFormData(initialFormData);
      setIncludeWeekends(enrollment.include_weekends || true);
      
      // Set selected date from enrollment
      if (enrollment.start_date) {
        const date = new Date(enrollment.start_date);
        if (!isNaN(date.getTime())) {
          setSelectedDate(date);
        }
      }

      // Fetch initial data
      await fetchInitialData(initialFormData.city_id, course.id);
      
      // Set course duration
      if (course.duration) {
        setSelectedCourseDuration(parseInt(course.duration) || 0);
      }

      setIsFormReady(true);
    } catch (error) {
      console.error("Error loading student data:", error);
    }
  };

  const fetchInitialData = async (cityId, courseId) => {
    try {
      // Fetch cities
      const citiesRes = await publicAuth("cities", "GET");
      const citiesList = citiesRes?.data?.data || citiesRes?.data?.cities || [];
      setCities(citiesList);

      // Fetch courses
      const coursesRes = await publicAuth("courses");
      const coursesList = coursesRes.data?.courses || coursesRes.data?.data || [];
      setCourses(coursesList);

      // If city is already selected, fetch schools
      if (cityId) {
        await fetchSchoolsForCity(cityId);
      }
      
    } catch (error) {
      console.error("Error fetching initial data:", error);
    }
  };

  // Fetch schools when city is selected
  useEffect(() => {
    if (formData.city_id && isFormReady) {
      fetchSchoolsForCity(formData.city_id);
    }
  }, [formData.city_id, isFormReady]);

  const fetchSchoolsForCity = async (cityId) => {
    try {
      setApiLoading(true);
      const res = await publicAuth("driving-schools", "POST", { city_id: cityId });
      const list = res?.data?.data || res?.data?.schools || [];
      setSchools(list);
    } catch (e) {
      console.error("schools fetch error:", e);
      setSchools([]);
    } finally {
      setApiLoading(false);
    }
  };

  const fetchTimeSlots = async (date) => {
    try {
      setApiLoading(true);
      const formattedDate = date.toISOString().split("T")[0];
      
      const res = await bearerAuth(`check-timeslots`, "POST", {
        date: formattedDate,
        assignSchool: formData.assignSchool,
      });
      
      let slots = [];
      if (res?.data?.slots) {
        slots = res.data.slots;
      } else if (res?.data?.timeslots) {
        slots = res.data.timeslots;
      } else if (res?.data?.data) {
        slots = res.data.data;
      }
      
      setTimeSlots(slots);
      
      // Try to select the existing time slot
      if (formData.admin_time_slot) {
        const normalizedCurrent = normalizeSlotText(formData.admin_time_slot);
        const existingSlot = slots.find(slot => 
          normalizeSlotText(`${slot.start_time} - ${slot.end_time}`) === normalizedCurrent
        );
        if (existingSlot) {
          setSelectedTimeSlot(existingSlot);
        }
      }
      
    } catch (err) {
      console.error("Time slots fetch error:", err);
      setTimeSlots([]);
    } finally {
      setApiLoading(false);
    }
  };

  const calculateDateRange = () => {
    if (!selectedCourseDuration) {
      setDateRange([]);
      return;
    }
    let daysToAdd = selectedCourseDuration;
    let date = new Date(selectedDate);
    let daysCounted = 0;
    let range = [new Date(date)];
    while (daysCounted < daysToAdd - 1) {
      date.setDate(date.getDate() + 1);
      if (includeWeekends || (date.getDay() !== 0 && date.getDay() !== 6)) {
        daysCounted++;
        range.push(new Date(date));
      }
    }
    setDateRange(range);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
    
    // If course is changed, update duration
    if (name === 'course_id' && value) {
      const selectedCourse = courses.find((c) => c.id === parseInt(value));
      if (selectedCourse) {
        setSelectedCourseDuration(parseInt(selectedCourse.duration) || 0);
      }
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const formattedDate = date.toISOString().split('T')[0];
    setFormData(prev => ({
      ...prev,
      admin_exact_start_at: formattedDate
    }));
  };

  const handleTimeSlotSelect = (slot) => {
    setSelectedTimeSlot(slot);
    const timeSlotValue = `${slot.start_time} - ${slot.end_time}`;
    setFormData(prev => ({
      ...prev,
      admin_time_slot: timeSlotValue
    }));
    
    // Clear error
    if (errors.admin_time_slot) {
      setErrors(prev => ({
        ...prev,
        admin_time_slot: null
      }));
    }
  };

  const toggleIncludeWeekends = () => {
    const newValue = !includeWeekends;
    setIncludeWeekends(newValue);
    setFormData(prev => ({
      ...prev,
      inc_sat_sun: newValue
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) newErrors.full_name = "Full name is required";
    if (!formData.email) newErrors.email = "Email is required";
    if (!formData.contact) newErrors.contact = "Contact is required";
    if (!formData.zipcode) newErrors.zipcode = "Zipcode is required";
    if (!formData.dob) newErrors.dob = "Date of birth is required";
    if (!formData.gender) newErrors.gender = "Gender is required";
    if (!formData.city_id) newErrors.city_id = "City is required";
    if (!formData.assignSchool) newErrors.assignSchool = "School is required";
    if (!formData.course_id) newErrors.course_id = "Course is required";
    if (!formData.admin_exact_start_at) newErrors.admin_exact_start_at = "Start date is required";
    if (!formData.admin_time_slot) newErrors.admin_time_slot = "Time slot is required";
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email && !emailRegex.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    // Contact validation (10 digits)
    const phoneRegex = /^[0-9]{10}$/;
    if (formData.contact && !phoneRegex.test(formData.contact.replace(/\D/g, ''))) {
      newErrors.contact = "Please enter a valid 10-digit phone number";
    }
    
    // Zipcode validation (6 digits for India)
    const zipcodeRegex = /^[1-9][0-9]{5}$/;
    if (formData.zipcode && !zipcodeRegex.test(formData.zipcode)) {
      newErrors.zipcode = "Invalid Indian ZIP code (6 digits required)";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        contact: formData.contact.toString(),
        course_id: parseInt(formData.course_id),
        city_id: parseInt(formData.city_id),
        assignSchool: formData.assignSchool,
        // Combine date and time for admin_exact_start_at
        admin_exact_start_at: `${formData.admin_exact_start_at} ${selectedTimeSlot?.start_time || '00:00:00'}`,
        student_id: student.id // Add student ID for edit
      };
      
      console.log("Updating student with payload:", payload);
      
      // Correct API endpoint with student ID in URL
      const response = await bearerAuth(
        `driving-school/edit-student/${student.id}`, 
        "POST", 
        payload
      );
      
      console.log("Edit student response:", response.data);
      
      if (response.data?.success || response.data?.status) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Student updated successfully!",
          showConfirmButton: false,
          timer: 1500
        });
        onClose();
      } else {
        Swal.fire("Error", response.data?.message || "Failed to update student", "error");
      }
    } catch (error) {
      console.error("Error updating student:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "An error occurred while updating student",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // Render day contents for calendar
  const renderDayContents = (day, date) => {
    const isInRange = dateRange.some((d) => d.toDateString() === date.toDateString());
    const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();
    return (
      <div
        style={{
          backgroundColor: isSelected ? "#0d6efd" : isInRange ? "#e6f2ff" : "transparent",
          fontWeight: isSelected ? "bold" : "normal",
          color: isSelected ? "#fff" : isInRange ? "#0d6efd" : "inherit",
          borderRadius: "50%",
          width: "100%",
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {day}
      </div>
    );
  };

  // Format time in 24-hour format
  const formatTime24Hour = (timeString) => {
    if (!timeString) return '';
    if (timeString.includes(':')) {
      const parts = timeString.split(':');
      if (parts.length === 3) {
        return timeString;
      } else if (parts.length === 2) {
        return `${timeString}:00`;
      }
    }
    return timeString;
  };

  const formatSlotLabel = (slot) => {
    const base = `${formatTime24Hour(slot.start_time)} - ${formatTime24Hour(slot.end_time)}`;
    const capacity = Number(slot.capacity || 0);
    const bookedCount = Number(slot.booked_count || 0);
    if (capacity > 0) {
      return `${base} (${bookedCount}/${capacity})${slot.booked ? " - Full" : ""}`;
    }
    return `${base}${slot.booked ? " - Full" : ""}`;
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>Edit Student</Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div className="row g-3">
            {/* Personal Information Section */}
            <div className="col-12 mb-4">
              <h5 className="text-primary mb-3 border-bottom pb-2">Personal Information</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Full Name *</Form.Label>
                    <Form.Control
                      type="text"
                      className={errors.full_name ? 'is-invalid' : ''}
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      placeholder="Enter full name"
                      disabled={loading || apiLoading}
                    />
                    {errors.full_name && <Form.Text className="text-danger">{errors.full_name}</Form.Text>}
                  </Form.Group>
                </div>
                
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Email *</Form.Label>
                    <Form.Control
                      type="email"
                      className={errors.email ? 'is-invalid' : ''}
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email"
                      disabled={loading || apiLoading}
                    />
                    {errors.email && <Form.Text className="text-danger">{errors.email}</Form.Text>}
                  </Form.Group>
                </div>
                
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Contact Number *</Form.Label>
                    <div className="input-group">
                      <span className="input-group-text">+91</span>
                      <Form.Control
                        type="tel"
                        className={errors.contact ? 'is-invalid' : ''}
                        name="contact"
                        value={formData.contact}
                        onChange={handleChange}
                        placeholder="Enter 10-digit contact number"
                        maxLength="10"
                        disabled={loading || apiLoading}
                      />
                    </div>
                    {errors.contact && <Form.Text className="text-danger">{errors.contact}</Form.Text>}
                  </Form.Group>
                </div>
                
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Zipcode *</Form.Label>
                    <Form.Control
                      type="text"
                      className={errors.zipcode ? 'is-invalid' : ''}
                      name="zipcode"
                      value={formData.zipcode}
                      onChange={handleChange}
                      placeholder="Enter zipcode"
                      maxLength="6"
                      disabled={loading || apiLoading}
                    />
                    {errors.zipcode && <Form.Text className="text-danger">{errors.zipcode}</Form.Text>}
                  </Form.Group>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Date of Birth *</Form.Label>
                    <Form.Control
                      type="date"
                      className={errors.dob ? 'is-invalid' : ''}
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      disabled={loading || apiLoading}
                    />
                    {errors.dob && <Form.Text className="text-danger">{errors.dob}</Form.Text>}
                  </Form.Group>
                </div>

                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Gender *</Form.Label>
                    <Form.Select
                      className={errors.gender ? 'is-invalid' : ''}
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                      disabled={loading || apiLoading}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                    {errors.gender && <Form.Text className="text-danger">{errors.gender}</Form.Text>}
                  </Form.Group>
                </div>
              </div>
            </div>

            {/* City and School Selection Section */}
            <div className="col-12 mb-4">
              <h5 className="text-primary mb-3 border-bottom pb-2">Location Details</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>City *</Form.Label>
                    <Form.Select
                      className={errors.city_id ? 'is-invalid' : ''}
                      name="city_id"
                      value={formData.city_id}
                      onChange={handleChange}
                      disabled={loading || apiLoading}
                    >
                      <option value="">Select City</option>
                      {cities.map(city => (
                        <option key={city.id} value={city.id}>
                          {city.name || city.city_name}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.city_id && <Form.Text className="text-danger">{errors.city_id}</Form.Text>}
                  </Form.Group>
                </div>
                
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Driving School *</Form.Label>
                    <div className="position-relative">
                      <Form.Select
                        className={errors.assignSchool ? 'is-invalid' : ''}
                        name="assignSchool"
                        value={formData.assignSchool}
                        onChange={handleChange}
                        disabled={!formData.city_id || schools.length === 0 || loading || apiLoading}
                      >
                        <option value="">Select Driving School</option>
                        {schools.map(school => (
                          <option key={school.key || school.id} value={school.key || school.id}>
                            {school.name} {school.city_name ? `(${school.city_name})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                      {apiLoading && schools.length === 0 && formData.city_id && (
                        <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                          <span className="spinner-border spinner-border-sm text-primary"></span>
                        </div>
                      )}
                    </div>
                    {errors.assignSchool && <Form.Text className="text-danger">{errors.assignSchool}</Form.Text>}
                    {formData.city_id && !apiLoading && schools.length === 0 && (
                      <Form.Text className="text-muted">No driving schools available for selected city</Form.Text>
                    )}
                  </Form.Group>
                </div>
              </div>
            </div>

            {/* Course and Enrollment Section */}
            <div className="col-12 mb-4">
              <h5 className="text-primary mb-3 border-bottom pb-2">Course Details</h5>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Course *</Form.Label>
                    <Form.Select
                      className={errors.course_id ? 'is-invalid' : ''}
                      name="course_id"
                      value={formData.course_id}
                      onChange={handleChange}
                      disabled={loading || apiLoading}
                    >
                      <option value="">Select Course</option>
                      {courses.map(course => (
                        <option key={course.id} value={course.id}>
                          {course.title || course.name || course.course_name} 
                          {course.duration ? ` (${course.duration} days)` : ''}
                          {course.price ? ` - ₹${course.price}` : ''}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.course_id && <Form.Text className="text-danger">{errors.course_id}</Form.Text>}
                  </Form.Group>
                </div>
                
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Enrollment For *</Form.Label>
                    <Form.Select
                      name="enrollment_for"
                      value={formData.enrollment_for}
                      onChange={handleChange}
                      disabled={loading || apiLoading}
                    >
                      {enrollmentOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </div>

            {/* Schedule Selection Section */}
            <div className="col-12 mb-4">
              <h5 className="text-primary mb-3 border-bottom pb-2">Schedule Selection</h5>
              
              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  name="inc_sat_sun"
                  label="Include Saturdays and Sundays"
                  checked={includeWeekends}
                  onChange={toggleIncludeWeekends}
                  disabled={!formData.course_id || loading || apiLoading}
                />
              </div>
              
              <div className="row">
                {/* Calendar Section */}
                <div className="col-md-5 mb-3">
                  <Form.Group>
                    <Form.Label>Start Date *</Form.Label>
                    <div className="card">
                      <div className="card-body">
                        <DatePicker
                          selected={selectedDate}
                          onChange={handleDateChange}
                          inline
                          className="w-100"
                          renderDayContents={renderDayContents}
                          dayClassName={(date) => {
                            const isInRange = dateRange.some(
                              (d) => d.toDateString() === date.toDateString()
                            );
                            return isInRange ? "react-datepicker__day--highlight-range" : "";
                          }}
                          disabled={loading || apiLoading}
                        />
                        {selectedCourseDuration > 0 && dateRange.length > 0 && (
                          <div className="mt-3 small text-muted">
                            <p className="mb-1">
                              <strong>Course Duration:</strong> {selectedCourseDuration} days
                            </p>
                            <p className="mb-0">
                              <strong>Period:</strong> {dateRange[0].toLocaleDateString()} to{" "}
                              {dateRange[dateRange.length - 1].toLocaleDateString()}
                              {!includeWeekends && " (excluding weekends)"}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.admin_exact_start_at && <Form.Text className="text-danger">{errors.admin_exact_start_at}</Form.Text>}
                  </Form.Group>
                </div>
                
                {/* Time Slots Section */}
                <div className="col-md-7 mb-3">
                  <Form.Group>
                    <Form.Label>Time Slot *</Form.Label>
                    <div className="card h-100">
                      <div className="card-body">
                        {!formData.assignSchool ? (
                          <div className="alert alert-warning mb-0">
                            Please select a driving school first
                          </div>
                        ) : timeSlots.length === 0 ? (
                          <div className="alert alert-warning mb-0">
                            No time slots available for selected date
                          </div>
                        ) : (
                          <div className="time-slots-container">
                            <div className="row g-2">
                              {timeSlots.map((slot) => (
                                <div key={slot.id} className="col-6">
                                  <Button
                                    variant={selectedTimeSlot?.id === slot.id ? "primary" : "outline-primary"}
                                    className="w-100 mb-2"
                                    onClick={() => handleTimeSlotSelect(slot)}
                                    disabled={(slot.booked === true && selectedTimeSlot?.id !== slot.id) || loading || apiLoading}
                                  >
                                    {formatSlotLabel(slot)}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {apiLoading && (
                          <div className="text-center mt-2">
                            <span className="spinner-border spinner-border-sm text-primary"></span>
                            <span className="ms-2">Loading time slots...</span>
                          </div>
                        )}
                      </div>
                    </div>
                    {errors.admin_time_slot && <Form.Text className="text-danger">{errors.admin_time_slot}</Form.Text>}
                  </Form.Group>
                </div>
              </div>
            </div>

            {/* Additional Information Section */}
            <div className="col-12 mb-4">
              <h5 className="text-primary mb-3 border-bottom pb-2">Additional Information</h5>
              <div className="row">
                <div className="col-12 mb-3">
                  <Form.Group>
                    <Form.Label>Admin Notes *</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="admin_note"
                      value={formData.admin_note}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Add any special instructions or notes"
                      disabled={loading || apiLoading}
                    />
                    {!formData.admin_note && <Form.Text className="text-muted">Admin notes are required</Form.Text>}
                  </Form.Group>
                </div>
                
                <div className="col-md-6 mb-3">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      disabled={loading || apiLoading}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={onClose}
            disabled={loading || apiLoading}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading || apiLoading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Updating...
              </>
            ) : 'Update Student'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditStudent;
